import PropTypes from "prop-types";
import React, { Component } from "react";
import { graphql } from "@apollo/client/react/hoc";
import compose from "lodash.flowright";
import { withRouter } from "react-router-dom";
import { withTranslation } from "react-i18next";

import Button from "../Button";

import { GET_AUTH } from "../../../graphql/resolvers/auth";
import { isLoggedIn } from "../../../helpers/auth";
import {
  releaseBuildYourOwnReservation,
  useBuildYourOwnAddGuard,
} from "../../../helpers/buildYourOwnCartLock";
import { FETCH_POLICY_CACHE_ONLY, I18N_NAMESPACES } from "../../../helpers/constants";
import { logError } from "../../../helpers/logging";
import { showErrorAlert, showSuccessAlert } from "../../../helpers/alerts";
import { GET_MEMBER_SHOPPING_CART } from "../../../graphql/queries";
import {
  ADD_SHOPPING_CART_ITEM,
  ADD_ITEM_TO_LOCAL_SHOPPING_CART,
  SHOW_LOCAL_SHOPPING_CART,
  DELETE_ITEMS_BY_PRODUCT_TYPE_IN_LOCAL_SHOPPING_CART,
  DELETE_PRODUCT_TYPE_SHOPPING_CART_ITEMS,
} from "../../../graphql/mutations";
import { AppContext, useLayoutContext } from "../../organisms/Layout/context";
import { buildLocalCartItemInput } from "../../../helpers/guestProductAdd";
import { AddToBoxOrCartSuccessMessage } from "../AddToBoxOrCartSuccessMessage";

/**
 * Renders AddProductToShoppingCartButton component that allows adding product to a Shopping Cart.
 * */
class AddProductToShoppingCartButton extends Component {
  static contextType = AppContext;

  static propTypes = {
    product: PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      productType: PropTypes.shape({}).isRequired,
    }).isRequired,
    quantity: PropTypes.number,
    label: PropTypes.string,
    authQuery: PropTypes.shape({}).isRequired,
    showShoppingCart: PropTypes.bool,
    setShouldOpenBoxAndCartTray: PropTypes.func.isRequired,
    showLocalShoppingCart: PropTypes.func.isRequired,
    callbackAfterAdd: PropTypes.func,
    callbackOnAddFail: PropTypes.func,
    addShoppingCartItem: PropTypes.func.isRequired,
    className: PropTypes.string,
    i18nKey: PropTypes.string,
    children: PropTypes.node,
    disabled: PropTypes.bool,
    extraInfo: PropTypes.shape({}),
    // Makes the success toast read "monthly box" instead of "one-off box"
    successMessageIsBox: PropTypes.bool,
    deleteSameProductType: PropTypes.bool,
    deleteItemsByProductTypeInLocalShoppingCart: PropTypes.func.isRequired,
    deleteProductTypeShoppingCartItems: PropTypes.func.isRequired,
    ButtonComponent: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.shape({})]),
    extraRefetchQueries: PropTypes.arrayOf(PropTypes.shape({})),

    // callbackBeforeAdd is called in parent component as soon as users clicks this button.
    callbackBeforeAdd: PropTypes.func,
    // Stops an add that a build-your-own advent order (wines only) cannot take, explaining why.
    isBlockedByBuildYourOwn: PropTypes.func,

    // Unlike callbackAfterAdd (fired even when the mutation returns errors),
    // onAddSuccess only fires on a successful add — for callers rendering
    // their own confirmation UI.
    onAddSuccess: PropTypes.func,
    // Fired whenever the add fails (rejected mutation, missing member id,
    // payload or resolver errors) — the paths callbackAfterAdd deliberately
    // skips. Lets callers unwind in-flight state they set in callbackBeforeAdd.
    callbackAfterError: PropTypes.func,
    // Set false when the caller renders its own confirmation instead of the
    // shared success toast.
    showSuccessToast: PropTypes.bool,
  };

  static defaultProps = {
    quantity: 1,
    label: "Add",
    showShoppingCart: true,
    callbackAfterAdd: null,
    callbackOnAddFail: () => {},
    className: "",
    i18nKey: "winePages:wineAdded",
    children: null,
    disabled: false,
    extraInfo: null,
    successMessageIsBox: false,
    deleteSameProductType: false,
    ButtonComponent: Button,
    callbackBeforeAdd: () => {},
    isBlockedByBuildYourOwn: () => false,
    onAddSuccess: null,
    callbackAfterError: null,
    showSuccessToast: true,
  };

  /**
   * Adds items to shopping cart
   * If user IS logged in, adds item to database's shopping cart
   * If user IS NOT logged in, adds item to browser local storage's shopping cart
   * @param {Object} product - product to be added
   * @return {Promise<void>}
   * */
  handleAddItemToShoppingCart = async (product) => {
    const { callbackBeforeAdd, isBlockedByBuildYourOwn, quantity: addQuantity } = this.props;
    const guardedLines = [{ product, quantity: addQuantity }];
    if (isBlockedByBuildYourOwn(guardedLines)) return;
    // Wine the guard let through is spoken for until the cart shows it; a failed add hands it back.
    this.guardedLines = guardedLines;
    callbackBeforeAdd();

    this.resolvePreSaveOperations(product).then(() => {
      const {
        quantity,
        authQuery,
        addShoppingCartItem,
        addItemToLocalShoppingCart,
        extraInfo,
        callbackAfterError,
      } = this.props;
      const { minBottlesForFreeShipping, standardShippingFee } = this.context;

      if (isLoggedIn()) {
        const memberId = authQuery.auth && authQuery.auth.memberId;
        if (!memberId) {
          this.releaseGuardedLines();
          logError("User is logged in, but does not have a memberId in local storage.");
          callbackAfterError && callbackAfterError();
        } else {
          // Saves product to shopping cart database in case user is logged in
          addShoppingCartItem({
            variables: {
              input: {
                memberId,
                productId: product.id,
                quantity,
                extraInfo,
              },
            },
          })
            .then((response) => {
              this.finaliseOperation(response, quantity);
            })
            .catch(this.handleAddError);
        }
      } else {
        // Saves product item to shopping cart in browser session in case user is not logged in
        const shoppingCartItem = buildLocalCartItemInput({
          product,
          quantity,
          extraInfo,
          minBottlesForFreeShipping,
          standardShippingFee,
        });
        addItemToLocalShoppingCart(shoppingCartItem)
          .then((response) => {
            this.finaliseOperation(response, quantity);
          })
          .catch(this.handleAddError);
      }
    });
  };

  releaseGuardedLines = () => {
    if (!this.guardedLines) return;
    releaseBuildYourOwnReservation(this.guardedLines);
    this.guardedLines = null;
  };

  handleAddError = (error) => {
    const { callbackOnAddFail, callbackAfterError, t } = this.props;
    this.releaseGuardedLines();
    logError(error);
    showErrorAlert(t("common:sorry"));
    callbackOnAddFail();
    /* A failed add never reaches the success path, so callers tracking an
     * in-flight state via callbackBeforeAdd would stay locked without this. A
     * separate callback from callbackAfterAdd, which callers use as "add
     * completed" (SendGift navigates to checkout in it). */
    callbackAfterError && callbackAfterError();
  };

  /**
   * Executes all callbacks after product has been successfully added to the Shopping Cart.
   *
   * @param {Object} response
   * @param {number} submittedQuantity - the quantity the mutation was actually
   * sent with. The `quantity` prop may have moved on while the request was in
   * flight (steppers stay interactive), and the success toast must report what
   * was added, not what is now selected.
   */
  finaliseOperation = (response, submittedQuantity) => {
    const {
      showShoppingCart,
      showLocalShoppingCart,
      callbackAfterAdd,
      callbackOnAddFail,
      t,
      setShouldOpenBoxAndCartTray,
      i18nKey,
      onAddSuccess,
      showSuccessToast,
      successMessageIsBox,
      callbackAfterError,
    } = this.props;
    const { data, errors: graphQLErrors } = response || {};
    const { addShoppingCartItem } = data || {};
    const errors = addShoppingCartItem?.errors?.length ? addShoppingCartItem.errors : response?.errors;
    if (errors?.length) {
      this.releaseGuardedLines();
      const errorMsg = errors[0].messages?.[0] || t("common:sorry");
      showErrorAlert(errorMsg, { autoClose: 10000 });
      logError(errorMsg);
      callbackOnAddFail();
      callbackAfterError && callbackAfterError();
      return;
    }
    if (graphQLErrors && graphQLErrors.length) {
      this.releaseGuardedLines();
      /* The client mutates with errorPolicy "all", so a resolver-level failure
       * RESOLVES with top-level errors and no payload — without this branch it
       * would fall through to the success toast + onAddSuccess with an
       * unchanged cart. */
      showErrorAlert(t("common:sorry"), { autoClose: 10000 });
      logError(graphQLErrors[0]?.message || "addShoppingCartItem returned GraphQL errors");
      callbackOnAddFail();
      callbackAfterError && callbackAfterError();
      return;
    }
    if (showSuccessToast) {
      const successMessage = (
        <AddToBoxOrCartSuccessMessage
          setShouldOpenBoxAndCartTray={setShouldOpenBoxAndCartTray}
          values={{ count: submittedQuantity || 1, isBox: successMessageIsBox }}
          i18nKey={i18nKey}
        />
      );
      showSuccessAlert(successMessage, {});
    }
    onAddSuccess && onAddSuccess(response);
    showShoppingCart && showLocalShoppingCart();
    callbackAfterAdd && callbackAfterAdd();
  };

  /**
   * Make any operations required before adding a product to the shopping cart.
   * @param {Object} product - product to be added
   * @return {Promise<void>}
   * */
  resolvePreSaveOperations = (product) => this.deleteItemsFromTheSameProductType(product);

  /**
   * Deletes any items from the shopping cart of the same productTypeId.
   * @param {Object} product - product to be added
   * @return {Promise<void>}
   * */
  deleteItemsFromTheSameProductType = (product) => {
    const {
      authQuery,
      deleteSameProductType,
      deleteProductTypeShoppingCartItems,
      deleteItemsByProductTypeInLocalShoppingCart,
    } = this.props;
    const { minBottlesForFreeShipping, standardShippingFee } = this.context;

    if (deleteSameProductType) {
      if (isLoggedIn()) {
        const memberId = authQuery.auth && authQuery.auth.memberId;
        if (!memberId) {
          logError("User is logged in, but does not have a memberId in local storage.");
        } else {
          // Deletes all items from the given Product Type from the member's shopping cart
          return deleteProductTypeShoppingCartItems({
            variables: {
              input: {
                memberId,
                productTypeId: product.productType.id,
              },
            },
          });
        }
      } else {
        // Deletes all items from the given productTypeId in the local shopping cart
        return deleteItemsByProductTypeInLocalShoppingCart({
          variables: {
            input: {
              productTypeId: product.productType.id,
              minBottlesForFreeShipping,
              standardShippingFee,
            },
          },
        });
      }
    }
    return Promise.resolve();
  };

  render() {
    const { product, label, className, ButtonComponent, children, ...otherProps } = this.props;

    return (
      <ButtonComponent
        className={className}
        type="button"
        onClick={() => this.handleAddItemToShoppingCart(product)}
        {...otherProps}
      >
        {children}
        {label}
      </ButtonComponent>
    );
  }
}

const AddProductToShoppingCartButtonWrapper = (props) => {
  const { setShouldOpenBoxAndCartTray } = useLayoutContext();
  const isBlockedByBuildYourOwn = useBuildYourOwnAddGuard();
  return (
    <AddProductToShoppingCartButton
      setShouldOpenBoxAndCartTray={setShouldOpenBoxAndCartTray}
      isBlockedByBuildYourOwn={isBlockedByBuildYourOwn}
      {...props}
    />
  );
};

export default compose(
  withRouter,
  graphql(GET_AUTH, {
    name: "authQuery",
    options: { fetchPolicy: FETCH_POLICY_CACHE_ONLY },
  }),
  graphql(ADD_SHOPPING_CART_ITEM, {
    name: "addShoppingCartItem",
    options: (props) => ({
      refetchQueries: () => [{ query: GET_MEMBER_SHOPPING_CART }, ...(props.extraRefetchQueries ?? [])],
    }),
  }),
  graphql(ADD_ITEM_TO_LOCAL_SHOPPING_CART, {
    name: "addItemToLocalShoppingCart",
  }),
  graphql(SHOW_LOCAL_SHOPPING_CART, { name: "showLocalShoppingCart" }),
  graphql(DELETE_ITEMS_BY_PRODUCT_TYPE_IN_LOCAL_SHOPPING_CART, {
    name: "deleteItemsByProductTypeInLocalShoppingCart",
  }),
  graphql(DELETE_PRODUCT_TYPE_SHOPPING_CART_ITEMS, {
    name: "deleteProductTypeShoppingCartItems",
    options: { refetchQueries: () => [{ query: GET_MEMBER_SHOPPING_CART }] },
  }),
  withTranslation([I18N_NAMESPACES.COMMON, I18N_NAMESPACES.WINE_PAGES])
)(AddProductToShoppingCartButtonWrapper);
